import { parseUtcDate } from '../../common/dateTime';
import { InventorySyncMinute, InventorySyncOverview } from '../../models/inventorySync';

/** Точка графика синка: сумма счётчиков минут, попавших в корзину */
export interface SyncChartPoint {
    x: Date;
    attempts: number;
    rateLimited: number;
    admissionRejected: number;
    publicLinkBatches: number;
    fallbackBatches: number;
}

export interface BreakdownRow {
    key: string;
    value: number;
    percent: number;
}

export interface OverviewTotals {
    attempts: number;
    rateLimited: number;
    /** 429 от HTTP-попыток, % */
    rateLimitedPercent: number;
    admissionRejected: number;
    publicLinkBatches: number;
    fallbackBatches: number;
    /** Страниц через fallback (steamapis, боты) от всех загруженных страниц, % */
    fallbackPercent: number;
    /** Последняя известная глубина очереди, сумма по процессам */
    queueDepth: number;
}

const MINUTE_MS = 60_000;

function percent(part: number, total: number): number {
    return total > 0 ? Math.round(part / total * 1000) / 10 : 0;
}

/** Сворачивает минутный ряд в корзины по bucketMinutes, выровненные по UTC (13:00, 13:10, …) */
export function rollupSeries(series: InventorySyncMinute[], bucketMinutes: number): SyncChartPoint[] {
    const bucketMs = Math.max(1, Math.floor(bucketMinutes)) * MINUTE_MS;
    const points: SyncChartPoint[] = [];
    let current: SyncChartPoint | undefined;

    for (const m of series) {
        const start = Math.floor(parseUtcDate(m.minute).getTime() / bucketMs) * bucketMs;
        if (!current || current.x.getTime() !== start) {
            current = { x: new Date(start), attempts: 0, rateLimited: 0, admissionRejected: 0, publicLinkBatches: 0, fallbackBatches: 0 };
            points.push(current);
        }
        current.attempts += m.attempts;
        current.rateLimited += m.rateLimited;
        current.admissionRejected += m.admissionRejected;
        current.publicLinkBatches += m.publicLinkBatches;
        current.fallbackBatches += m.fallbackBatches;
    }
    return points;
}

/** Словарь разбивки → строки по убыванию значения с долей от суммы */
export function breakdownRows(values: Record<string, number> | null | undefined): BreakdownRow[] {
    const entries = Object.entries(values ?? {});
    const total = entries.reduce((acc, [, v]) => acc + v, 0);
    return entries
        .sort(([ak, av], [bk, bv]) => bv - av || ak.localeCompare(bk))
        .map(([key, value]) => ({ key, value, percent: percent(value, total) }));
}

/** Имя процесса из IHostEnvironment.ApplicationName без префикса решения */
export function shortProcessName(name: string): string {
    return name.replace(/^SteamTrader\./, '');
}

export function overviewTotals(overview: InventorySyncOverview): OverviewTotals {
    const sum = (selector: (m: InventorySyncMinute) => number) =>
        overview.series.reduce((acc, m) => acc + selector(m), 0);

    const attempts = sum(m => m.attempts);
    const rateLimited = sum(m => m.rateLimited);
    const publicLinkBatches = sum(m => m.publicLinkBatches);
    const fallbackBatches = sum(m => m.fallbackBatches);

    return {
        attempts,
        rateLimited,
        rateLimitedPercent: percent(rateLimited, attempts),
        admissionRejected: sum(m => m.admissionRejected),
        publicLinkBatches,
        fallbackBatches,
        fallbackPercent: percent(fallbackBatches, publicLinkBatches + fallbackBatches),
        queueDepth: Object.values(overview.queueDepthByProcess ?? {}).reduce((acc, v) => acc + v, 0),
    };
}
